import {
  PaymentMethod,
  SelectOption,
  SelectOptionKey,
  SystemPackage,
  CustomPackage
} from "resources/assets/types";

import CustomPackageComponent from "../../components/CustomPackage.vue";
import SystemPackageComponent from "../../components/SystemPackage.vue";
import Vue from "vue";
import axios from "axios";
import isNil from "lodash/isNil";
import shortid from "shortid";

const NEW_PAYMENT_METHOD_KEY = "other";

const isSystemPackage = (
  p: SystemPackage | CustomPackage
): p is SystemPackage => !p.user_created;

const isCustomPackage = (
  p: SystemPackage | CustomPackage
): p is CustomPackage => !isSystemPackage(p);

export const PageComponent = Vue.extend({
  props: {
    initialPaymentMethod: {
      type: Number as () => SelectOptionKey | null,
      required: false
    },
    initialNewPaymentMethod: {
      type: String,
      required: false
    }
  },

  data() {
    return {
      paymentMethod: !isNil(this.initialNewPaymentMethod)
        ? NEW_PAYMENT_METHOD_KEY
        : this.initialPaymentMethod,
      newPaymentMethod: this.initialNewPaymentMethod,
      paymentMethods: [] as PaymentMethod[],
      packages: [] as (SystemPackage | CustomPackage)[]
    };
  },

  created() {
    this.loadPaymentMethodsAndPackages();
  },

  computed: {
    paymentMethodOptions(): SelectOption[] {
      const paymentMethods: SelectOption[] = this.paymentMethods.map(p => ({
        key: Number(p.id),
        value: Number(p.id),
        label: p.description
      }));

      paymentMethods.push({
        key: NEW_PAYMENT_METHOD_KEY,
        value: -1,
        renderOnly: true,
        label: "Other"
      } as SelectOption);

      return paymentMethods;
    },

    isAddingNewPaymentMethod(): boolean {
      return this.paymentMethod === NEW_PAYMENT_METHOD_KEY;
    },

    isValid() {
      if (!this.paymentMethod) {
        // a payment method must be selected.
        return false;
      }

      // all required system packages must have a rate
      if (
        !this.packages
          .filter(p => !p.user_created && p.required)
          .every(p => (p?.rate || 0) >= 0)
      ) {
        return false;
      }

      // all user-created packages must have both a description and a rate
      if (
        !this.packages
          .filter(p => p.user_created)
          .every(
            p =>
              (p?.description || "").length &&
              (p?.rate || 0) >= 0 &&
              Number(p.duration) > 0
          )
      ) {
        return false;
      }

      // if the user is adding a new payment method, one must actually be present
      if (
        this.paymentMethod === NEW_PAYMENT_METHOD_KEY &&
        (this.newPaymentMethod || "").length <= 0
      ) {
        return false;
      }

      return true;
    },

    systemPackages(): SystemPackage[] {
      return this.packages.filter(isSystemPackage);
    },

    customPackages(): CustomPackage[] {
      return this.packages.filter(isCustomPackage);
    }
  },

  methods: {
    /**
     * Remove a package based on it's id. Only user-created packages are removed
     * because there is never a reason to remove system packages.
     *
     * @param packageId THe id of the package to remove.
     */
    removePackage(packageId: number | string) {
      this.packages = this.packages.filter(
        p => !p.user_created || p.id !== packageId
      );
    },

    updateNewPaymentMethod(value: string) {
      this.newPaymentMethod = value;
    },

    updatePackage(thePackage: SystemPackage | CustomPackage) {
      const index = this.packages.findIndex(p => p.id === thePackage.id);

      if (index < 0) {
        throw new Error(
          `Failed to update package with id [${thePackage.id}]. It could not be found in the list of packages.`
        );
      }

      this.packages.splice(index, 1, thePackage);
    },

    updatePaymentMethod(key: SelectOptionKey) {
      this.paymentMethod =
        this.paymentMethodOptions.find(c => c.key === key)?.key || null;
    },

    async loadPaymentMethodsAndPackages() {
      const [paymentMethods, packages] = await Promise.all([
        axios.get("/ajax/payment-methods"),
        axios.get("/ajax/packages")
      ]);

      this.paymentMethods = paymentMethods?.data?.data || [];
      this.packages = packages?.data?.data || [];
    },

    addPackage() {
      this.packages.push({
        id: shortid.generate(),
        user_created: true,
        duration: 15,
        required: false
      });
    }
  },

  components: {
    CustomPackage: CustomPackageComponent,
    SystemPackage: SystemPackageComponent
  }
});
